import type {
  LifeRecord,
  EarthRow,
  EarthDivergence,
  LifeCareerStage,
  LifeFuturePath,
  LifeTimelineEntry,
  LifeBeat,
  EarthDimension,
  InterestId,
} from "../../types";
import type { NarrativeContext, NarrativeResult } from "../../narrative";
import { ENGINE_VERSION } from "../../lifeChapters";
import { lifeStream, ageFor, clampLevel, pick, pickDistinct, listJoin } from "./lifeHelpers";
import { ASCENDANT_CIVILIZATIONS, type CivilizationModel } from "./ascendant.life.banks";

/**
 * Builds a valid, fully populated, rich LifeRecord for the Ascendant Self of the Multiverse Life Dossier.
 * High-tier speculative-fiction civilization with radically advanced technologies, unfamiliar professions,
 * non-monetary credit/prestige systems, and real systemic tradeoffs.
 *
 * Fully deterministic: draws randomness exclusively via ctx.stream("life.<label>").
 */
export function buildAscendantLife(ctx: NarrativeContext, base: NarrativeResult): LifeRecord {
  const { resolved, universe, condition, input } = ctx;

  // 1. Pick a cohesive civilization model (8 distinct options in banks)
  const civ = pick(ASCENDANT_CIVILIZATIONS, lifeStream(ctx, "civ"));

  // 2. Identity
  const age = ageFor(lifeStream(ctx, "age"), 38, 76);
  const ageNoteOptions = [
    `Standard chronological solar years maintained in peak physical vigor through cellular rejuvenation and telomerase therapy.`,
    `Chronological orbital cycles recorded since emergence from the civic decanting nurseries of ${universe.name}.`,
    `Solar-equivalent years counted by the central pulsar beacon, stabilized through low-level continuous metabolic repair.`,
    `Measured in standard interstellar cycles, with biological senescence halted at optimal mental and physical vitality.`,
    `Solar revolutions of the primary star, preserved in a biological prime state through non-invasive cellular renewal.`,
    `Cycles since civic accreditation, with cellular aging held in permanent equilibrium by regenerative medical protocols.`,
    `Chronological years calculated from the baseline planetary calendar, with bodily aging neutralized by molecular biology.`,
    `Standard sidereal years, supported by gentle periodic rejuvenation therapies that preserve memory and physical grace.`,
  ];
  const ageNote = pick(ageNoteOptions, lifeStream(ctx, "age_note"));

  // Biography: 4-5 paragraphs, each 2-4 sentences
  const bioP1Options = [
    `${base.name} emerged into consciousness within the majestic architecture of ${civ.name}, where daily life is governed by ${civ.wealthConcept.toLowerCase()}. Early youth in the orbital nursery was steeped in the collective values of ${civ.culture.toLowerCase()} From earliest awakening, ${base.name} exhibited an instinctive curiosity toward large-scale systems and cosmic equilibrium.`,
    `Formed in the heart of ${civ.name}, ${base.name} grew up surrounded by the monumental physical and computational structures of ${universe.name}. Rather than pursuing passive luxury in an era of boundless automation, ${base.name} was drawn to the ethical weight of public guardianship. Those who watched ${base.name} develop noted an uncommon balance of analytical clarity and compassionate restraint.`,
    `The formative years of ${base.name} unfolded within ${civ.name}, amidst an advanced society that had long since abandoned the chaotic market scrambles of ancient epochs. Surrounded by mentors who thought in centuries rather than fiscal quarters, ${base.name} absorbed the foundational disciplines of systemic stewardship. This upbringing instilled a lasting reverence for conscious life and the delicate physical balances that sustain it.`,
    `Born into the sweeping vistas of ${civ.name}, ${base.name} came of age during an era of unprecedented cosmic stability across ${universe.name}. Early education emphasized that true civilizational greatness is demonstrated by what an empire chooses not to exploit. This philosophy became the guiding beacon that would steer every subsequent vocational and personal decision.`,
    `Within the luminous precincts of ${civ.name}, ${base.name} spent adolescent cycles exploring the intersection of natural philosophy and macro-scale engineering. Early family traditions emphasized accountability to future generations and an unshakeable duty to the living commonwealth. These foundational principles grounded ${base.name} as their technical and civic talents began to draw public recognition.`,
    `The life of ${base.name} took shape in ${civ.name}, where physical matter and social purpose are woven into a unified cultural fabric. Growing up in close proximity to massive civic works, ${base.name} learned early that immense technological power requires equally formidable moral discipline. This deep-seated conviction shielded them from the hubris that had unmade earlier technological empires.`,
    `Emerging from the civic educational academies of ${civ.name}, ${base.name} was recognized early for possessing a mind capable of synthesizing vast, disparate data streams into ethical action. In a society where material want had been extinct for generations, ${base.name} sought purpose through deliberate public service. This deliberate orientation toward collective flourishing defined the opening chapter of their adult trajectory.`,
    `Raised beneath the radiant canopies of ${civ.name}, ${base.name} was profoundly shaped by the collective memory of the Great Transition that established their society. Taught by elders who remembered the fragility of uncoordinated worlds, ${base.name} developed a profound respect for institutional transparency and open science. This early devotion paved the way for their formal entry into the high orders of civil stewardship.`,
  ];

  const bioP2Options = [
    `As an adult, ${base.name} dedicated their primary focus to the demanding trade of ${base.occupation.toLowerCase()}. Working within ${civ.institutions}, they quickly demonstrated an exceptional ability to resolve intricate systemic tensions where others saw only intractable bottlenecks. Their reputation grew not through political maneuvering, but through consistent, verifiable contributions to civilizational resilience.`,
    `Stepping into the specialized responsibilities of ${base.occupation.toLowerCase()}, ${base.name} took on tasks that directly safeguarded millions of citizens across ${universe.name}. Whether calibrating critical physical conduits or arbitrating delicate resource balances, their work was characterized by rigorous empirical precision. Peers across the assembly came to rely upon ${base.name}'s unhurried judgment during moments of technical stress.`,
    `In assuming the office of ${base.occupation.toLowerCase()}, ${base.name} channeled their natural talents into the day-to-day stewardship of vital infrastructure. They refused to treat their position as a comfortable administrative sinecure, spending long hours inspecting physical systems and engaging directly with junior apprentices. This grounded approach earned ${base.name} deep trust across both technical guilds and citizen assemblies.`,
    `The path of ${base.occupation.toLowerCase()} led ${base.name} to confront the subtle, complex challenges that arise in a post-scarcity civilization. They spearheaded crucial refinements in ${String(civ.technologies[0] ?? "").slice(0, 45).toLowerCase()} that prevented systemic drift across multiple sectors. Their leadership demonstrated that technological perfection is not static, but an active, daily discipline of vigilance and care.`,
    `Operating as ${base.occupation.toLowerCase()}, ${base.name} became a vital bridge between theoretical modelers and field engineers on the perimeter. They championed open-access diagnostic protocols that allowed local communities to participate directly in infrastructure maintenance. This democratic ethos solidified their standing as an indispensable guardian of the public interest.`,
    `Over several decades of rigorous practice in ${base.occupation.toLowerCase()}, ${base.name} navigated the delicate balance between rapid technological innovation and long-term safety. They frequently cautioned colleagues against premature deployment of unvalidated systems, earning a reputation as the conscience of the department. Their insistence on ethical thoroughness saved the commonwealth from numerous unforeseen systemic failures.`,
    `Through disciplined commitment to ${base.occupation.toLowerCase()}, ${base.name} established new standards of craft that were subsequently codified across multiple orbital sectors. They approached each operational challenge with the patience of a master artisan and the analytical acumen of a top-tier scientist. Even ideological rivals acknowledged the undeniable elegance and durability of their completed works.`,
    `As ${base.occupation.toLowerCase()}, ${base.name} directed critical initiatives that strengthened the physical and social links binding the settlements of ${universe.name}. They traveled extensively between central hubs and remote perimeter stations, ensuring that frontier outposts received equitable technical support and resource allocations. This hands-on devotion forged an enduring network of loyal allies throughout the star system.`,
  ];

  const bioP3Options = [
    `The defining crucible of their mid-career arrived when systemic crisis tested their resolve. ${(base.story?.turningPoint ?? "").slice(0, 160)}. Faced with high stakes and conflicting pressures from competing factions, ${base.name} stood firm on ethical first principles, choosing long-term civilizational stability over immediate political convenience. The successful resolution of this trial established their permanent legacy across the federation.`,
    `A pivotal historical moment reshaped their life when unexpected circumstances demanded immediate, courageous leadership. ${(base.story?.turningPoint ?? "").slice(0, 160)}. Rather than retreating behind bureaucratic protocols, ${base.name} stepped forward to implement a bold, innovative solution that protected vulnerable lives. The outcome cemented their reputation as a leader of extraordinary moral courage.`,
    `During a period of widespread uncertainty across the sector, ${base.name} faced their most formidable operational challenge. ${(base.story?.turningPoint ?? "").slice(0, 160)}. By mobilizing diverse talents and maintaining absolute transparency throughout the ordeal, they turned a potential catastrophe into a historic triumph for public cooperation. This decisive intervention earned them the lasting gratitude of the entire assembly.`,
    `Their career reached its defining zenith during the great institutional confrontation that shook the foundation of their order. ${(base.story?.turningPoint ?? "").slice(0, 160)}. Refusing to compromise on core humanist values, ${base.name} rallied colleagues to defend the principles of open stewardship against centralized encroachment. Their victory ensured that the benefits of cosmic technology remained democratically shared by all citizens.`,
    `When unexpected technical and ecological anomalies threatened the stability of the sector, ${base.name} was thrust into the center of the storm. ${(base.story?.turningPoint ?? "").slice(0, 160)}. Their calm, methodical de-escalation of the crisis prevented panic and preserved irreplaceable scientific and cultural assets. The event marked their formal transition from senior practitioner to revered civic elder.`,
    `The trajectory of ${base.name} was forever altered by a decisive choice that required sacrificing personal ease for collective safety. ${(base.story?.turningPoint ?? "").slice(0, 160)}. They undertook demanding field responsibilities that others avoided, demonstrating an exemplary willingness to share the hardships of the frontline crews. The enduring respect born of that trial continues to elevate their voice in highest council chambers.`,
    `A historic turning point occurred when deep-seated policy disagreements threatened to fracture the unity of the regional assembly. ${(base.story?.turningPoint ?? "").slice(0, 160)}. Through tireless personal diplomacy and unassailable technical evidence, ${base.name} forged a principled compromise that reconciled opposing factions without sacrificing safety. This diplomatic masterstroke established their authority as a master consensus builder.`,
    `In the midst of an unprecedented environmental and computational divergence, ${base.name} took decisive command of emergency response operations. ${(base.story?.turningPoint ?? "").slice(0, 160)}. Their innovative restructuring of emergency supply lines stabilized living conditions for millions of residents within days. This historic achievement remains a benchmark case study taught in civic academies today.`,
  ];

  const bioP4Options = [
    `Today, carrying the honored mantle of ${base.title}, ${base.name} continues to guide the grand trajectories of ${universe.name} with serene, unhurried wisdom. Though elevated to high civic standing, they maintain the modest habits and personal warmth that have always characterized their domestic life. Colleagues and apprentices alike seek out their counsel, knowing that every recommendation will be grounded in compassion, empirical truth, and deep-time vision.`,
    `In their present epoch, ${base.name} operates as a senior guardian whose influence extends far beyond formal institutional boundaries. Honored as ${base.title}, they dedicate substantial hours to mentoring the next generation of stewards while continuing to oversee macro-scale system calibrations. Their life stands as a living demonstration that immense technological mastery can exist in harmonious balance with genuine human gentleness.`,
    `Now universally recognized as ${base.title}, ${base.name} divides their time between high-level policy deliberation and quiet contemplative scholarship. They remain actively engaged in addressing the enduring challenge of ${civ.scarcityTradeoff.toLowerCase()} refusing to let the commonwealth rest on past laurels. Their steadfast presence provides an anchor of stability in an ever-expanding cosmic civilization.`,
    `Standing at the summit of their professional trajectory, ${base.name} bears the title of ${base.title} with quiet dignity and profound humility. They view their accumulated honors not as personal possessions, but as instruments of service to be deployed for the protection of fragile consciousness. In an era of boundless horizons, they remind all who will listen that the heart of wisdom is the preservation of wonder and empathy.`,
    `Today, ${base.name} exercises executive stewardship from the elevated vantage of ${base.title}, overseeing strategic initiatives that will mature across upcoming centuries. Despite bearing heavy responsibilities, they take time daily to walk among ordinary citizens, listening attentively to grassroots perspectives. This unbroken connection to everyday life ensures that their grandest plans remain firmly rooted in the lived reality of the people.`,
    `As ${base.title}, ${base.name} has achieved a rare synthesis of profound technical mastery, philosophical depth, and unyielding civic integrity. They continue to actively guide the operational doctrines of ${civ.name}, ensuring that institutional power never eclipses human dignity. Those who work beside ${base.name} know they are witnessing a life dedicated entirely to the lasting flourishing of conscious minds.`,
    `Carrying forward the esteemed responsibilities of ${base.title}, ${base.name} remains an indispensable pillar of social cohesion across the orbital sectors of ${universe.name}. They spend regular hours in dialogue with junior researchers and citizen assemblies, demystifying complex cosmic mechanics and fostering democratic engagement. Their enduring legacy continues to unfold with the calm inevitability of starlight across the void.`,
    `In this mature phase of life, ${base.name} embodies the finest aspirations of their civilization under the title of ${base.title}. They view each new dawn as a fresh opportunity to refine the balance between human ambition and cosmic reverence. Surrounded by trusted companions and respected by peers across star systems, they look toward the deep future with unwavering hope and quiet resolve.`,
  ];

  const bioP5Options = [
    `Looking toward the centuries ahead, ${base.name} remains focused on expanding the horizon of possibilities for future generations while fiercely safeguarding the core values of their youth. Their daily routine reflects a deliberate harmony between demanding cosmic duties, intellectual exploration, and intimate domestic happiness. In a multiverse of infinite branches, this self represents a triumphant convergence of intellect, ethics, and enduring purpose.`,
    `As the stellar cycles turn, ${base.name} continues to embody the peaceful, ambitious spirit of a civilization that has transcended scarcity without losing its soul. They approach each emerging technological frontier not with reckless haste, but with the patient reverence of a master artisan tending a generational garden. Their journey reminds all who observe it that the greatest achievement of conscious life is the deliberate choice to act with love and responsibility.`,
    `The ongoing chronicle of ${base.name} is a testament to the power of principled stewardship in an age of colossal cosmic forces. They remain deeply committed to ensuring that the gifts of ${universe.name} are preserved intact for descendants whose faces they will never see. In their quiet strength and generous spirit, the highest ideals of the Ascendant Self find their truest and most radiant expression.`,
    `Rooted in the timeless values of their order, ${base.name} stands as an inspiring exemplar of what human consciousness can achieve when liberated from artificial division and material dread. Their eyes remain fixed upon the distant stars, yet their hands remain dedicated to the immediate welfare of those beside them. Their story continues to illuminate the path toward a future where wisdom and power dwell together in enduring peace.`,
  ];

  const biography = [
    pick(bioP1Options, lifeStream(ctx, "bio_p1")),
    pick(bioP2Options, lifeStream(ctx, "bio_p2")),
    pick(bioP3Options, lifeStream(ctx, "bio_p3")),
    pick(bioP4Options, lifeStream(ctx, "bio_p4")),
    pick(bioP5Options, lifeStream(ctx, "bio_p5")),
  ];

  // 3. Work & Expertise
  const expertiseBank: Record<InterestId, string[]> = {
    science: [
      "Circumstellar magnetohydrodynamic stabilization",
      "Sub-quantum field resonance telemetry",
      "Relativistic chronometry and frame-dragging optics",
      "Macro-astrophysical thermodynamic modeling",
      "Non-linear plasma confinement diagnostics",
      "High-energy particle beam collimation",
    ],
    art: [
      "Spectrographic light architecture and aurora sculpting",
      "Macro-structural crystalline design aesthetics",
      "Kinetic zero-gravity balance choreography",
      "Multi-spectral public space illumination",
      "Interferometric visual projection matrices",
      "Bio-luminescent urban landscape integration",
    ],
    music: [
      "Pulsar frequency modulation and acoustical tuning",
      "Magnetospheric harmonic resonance synthesis",
      "Acoustic spatial dampening in pressurized hulls",
      "Sub-audible structural vibration diagnostics",
      "Interstellar radio wave compositional encoding",
      "Algorithmic choral synthesis for collective assemblies",
    ],
    nature: [
      "Closed-loop macro-biosphere ecological modeling",
      "Extremophile lichen and mycorrhizal agronomy",
      "Orbital arboretum atmospheric recycling",
      "Planetary carbon-nitrogen cycle equilibrium",
      "Micro-gravity soil hydrology and nutrient flow",
      "Synthetic evolutionary genetics for harsh biomes",
    ],
    technology: [
      "Dyson-swarm autonomous telemetry coordination",
      "Megastructure dynamic tension dampening",
      "Room-temperature computronium lattice routing",
      "Automated molecular matter-reconfiguration",
      "Relativistic inertial dampener calibration",
      "Passive superconducting energy distribution",
    ],
    people: [
      "Noospheric consensus mediation and dispute resolution",
      "Post-scarcity resource distribution ethics",
      "Inter-generational colonial integration policy",
      "Cognitive diversity protection frameworks",
      "Participatory assembly procedural architecture",
      "Cross-cultural reconciliation across star systems",
    ],
    stories: [
      "Deep-time civilizational chronicle curation",
      "Sapphire-crystal archival transcription protocols",
      "Historical anomaly investigation and synthesis",
      "Oral genealogy preservation across orbital rings",
      "Epistemic validation of ancient historical records",
      "Narrative architecture for inter-generational memory",
    ],
    adventure: [
      "Relativistic void corridor reconnaissance",
      "Accretion disk navigation and boundary piloting",
      "Gravitational wave anomaly sounding",
      "Deep-stellar atmospheric probe deployment",
      "Dark-matter filament trajectory pathfinding",
      "Frontier orbital habitat emergency survival triage",
    ],
  };

  const primaryInterest = resolved.interests[0] || "science";
  const secondaryInterest = resolved.interests[1] || (primaryInterest === "science" ? "technology" : "science");
  const expPicks1 = pickDistinct(expertiseBank[primaryInterest], 3, lifeStream(ctx, "exp_1"));
  const expPicks2 = pickDistinct(expertiseBank[secondaryInterest], 2, lifeStream(ctx, "exp_2"));
  const expertise = Array.from(new Set([...expPicks1, ...expPicks2])).slice(0, 5);

  const careerHistory: LifeCareerStage[] = [
    {
      stage: "Foundational Apprenticeship",
      years: "Cycles 1–6",
      text: `Integrated into the primary training academies of ${civ.name}, mastering foundational field mathematics and ${civ.wealthConcept.toLowerCase()} under veteran mentors.`,
    },
    {
      stage: "Independent Operational Specialist",
      years: "Cycles 7–16",
      text: `Assumed full field responsibility across orbital sectors, executing complex technical diagnostics and refining protocols in ${base.occupation.toLowerCase()}.`,
    },
    {
      stage: "Senior Guild Lead & System Overseer",
      years: "Cycles 17–28",
      text: `Directed regional initiatives managing critical infrastructure assets, publishing authoritative peer-reviewed methodologies adopted across multiple orbital rings.`,
    },
    {
      stage: "High Directorate & Stewardship Elder",
      years: "Cycles 29–Present",
      text: `Now operates as ${base.title}, arbitrating civilization-scale policy directions and guiding the long-horizon development of ${universe.name}.`,
    },
  ];

  const responsibilitiesOptions = [
    `Overseeing day-to-day diagnostic assessments and telemetry routing in ${base.occupation.toLowerCase()}.`,
    `Harmonizing energy fluxes and resource quotas across three municipal sectors within thermodynamic safety caps.`,
    `Directing a cohort of junior apprentices and post-graduate fellows in empirical systems verification.`,
    `Arbitrating technical and jurisdictional disputes between neighboring habitat councils with absolute impartiality.`,
    `Reviewing long-horizon climate and structural telemetry to preemptively mitigate micro-fractures and system drift.`,
    `Authorizing emergency allocations of compute slices and energy reserves during anomalous stellar flare activity.`,
    `Serving as chief liaison between field engineering crews and the elected citizen legislative assembly.`,
    `Validating open-access public blueprints before automated molecular fabrication bays deploy them system-wide.`,
  ];
  const responsibilities = pickDistinct(responsibilitiesOptions, 5, lifeStream(ctx, "resp"));

  const workplaceOptions = [
    `The High Spire Apex: an aerogel-and-diamond lattice chamber cantilevered into open vacuum with sweeping panoramas of ${universe.name}'s central star.`,
    `The Central Assembly Gallery: an open-air amphitheater surrounded by living vertical orchards and whispering quantum computing fountains.`,
    `The Orbital Operations Concourse: a pressurized geodesic ring hub suspended along the primary superconducting energy conduit.`,
    `The Deep Archives Rotunda: a vaulted granite sanctuary insulated deep within a hollowed asteroid, filled with sapphire data crystal racks.`,
    `The Telemetry Observation Deck: a vibration-dampened crystalline terrace overlooking the gentle dance of thirty thousand solar collector arrays.`,
    `The Keystone Engineering Pavilion: a light-filled modular complex hovering five hundred kilometers above the planetary cloud tops.`,
    `The Maritime Synthesis Platform: a buoyant white-stone atoll floating upon the tranquil waters of a newly terraformed planetary ocean.`,
    `The Sub-Orbital Transit Nexus: an elegant magnetic interchange station connecting high-speed vacuum shuttles with surface elevators.`,
  ];
  const workplace = pick(workplaceOptions, lifeStream(ctx, "workplace"));

  // 4. Standing & Influence
  const influenceScore = clampLevel(ctx.axes.influence, 35, 95);
  const wealthBracket = civ.wealthBracket;
  const wealthText = pick(civ.wealthDetails, lifeStream(ctx, "wealth_text"));
  const socialClass = pick(civ.socialClasses, lifeStream(ctx, "social_class"));
  const reputation = pick(civ.reputations, lifeStream(ctx, "reputation"));
  const prestige = pick(civ.prestiges, lifeStream(ctx, "prestige"));
  const influence = pick(civ.influences, lifeStream(ctx, "influence"));
  const economicCircumstances = pick(civ.economicCircumstances, lifeStream(ctx, "econ"));

  // 5. Home & Daily Life
  const dwelling = pick(civ.dwellings, lifeStream(ctx, "dwelling"));
  const environment = pick(civ.environments, lifeStream(ctx, "environment"));
  const lifestyle = pick(civ.lifestyles, lifeStream(ctx, "lifestyle"));
  const transport = pick(civ.transports, lifeStream(ctx, "transport"));
  const possessions = pickDistinct(civ.possessionsPool, 5, lifeStream(ctx, "possessions"));

  const routine = [
    pick(civ.routineSlots.slot1, lifeStream(ctx, "r1")),
    pick(civ.routineSlots.slot2, lifeStream(ctx, "r2")),
    pick(civ.routineSlots.slot3, lifeStream(ctx, "r3")),
    pick(civ.routineSlots.slot4, lifeStream(ctx, "r4")),
    pick(civ.routineSlots.slot5, lifeStream(ctx, "r5")),
    pick(civ.routineSlots.slot6, lifeStream(ctx, "r6")),
  ];

  // 6. People & Connections
  // Reusing relationships from base.dossier.relationships: [0]=mentor, [1]=rival, [2]=companion
  const relMentor = base.dossier.relationships[0]?.who || "Arch-Arbiter Thalor Vael";
  const relRival = base.dossier.relationships[1]?.who || "Director Caelum Rhaes";
  const relCompanion = base.dossier.relationships[2]?.who || "Vespera Dawn";

  const familyOptions = [
    `Belongs to an extended lineage of stellar architects and botanical stewards whose shared lineage traces across three orbital generations.`,
    `Maintains close, affectionate ties with an adopted kinship circle formed during their early apprenticeship in the outer rings.`,
    `Connected to a diverse multi-generational family unit residing across adjacent toroidal sectors, reuniting for seasonal planetary equinox festivals.`,
    `Part of a cooperative residential hearth sharing domestic duties, childcare, and a sprawling terraced citrus grove.`,
    `Keeps regular communication with siblings who navigate interstellar survey vessels, exchanging handwritten dispatches via laser relay.`,
    `Rooted in an ancient family covenant of scholars who have tended public educational academies since the Great Transition.`,
    `Shares a serene residential compound with several generations of partners and descendants, bounded by mutual respect and creative autonomy.`,
    `Honors a lineage of pioneering terraformers whose names are inscribed on the founding obelisks of four colonized moons.`,
  ];
  const family = pick(familyOptions, lifeStream(ctx, "people_family"));

  const relTextOptions = [
    `Shares a deep, enduring partnership with ${relCompanion}, whose grounded emotional clarity balances ${base.name}'s demanding civic responsibilities. Their mutual trust forms an unshakeable domestic anchor.`,
    `United in a collaborative life bond with ${relCompanion}; together they designed their home's micro-climate gardens, finding joy in shared intellectual and practical labor.`,
    `Deeply attuned to ${relCompanion}, sharing long evening conversations on the terrace while reviewing the quiet progress of the day. Their partnership is built on equal dignity and creative freedom.`,
    `Maintains a warm, devoted relationship with ${relCompanion}, whose artistic and practical insight provides honest perspective during intense political assemblies.`,
  ];
  const relationshipsText = pick(relTextOptions, lifeStream(ctx, "people_rel"));

  const friendOptions = [
    `Maintains an intellectually stimulating friendship with elder mentor ${relMentor}, while engaging in principled public debate with institutional rival ${relRival}.`,
    `Keeps a vibrant circle of close companions including long-time confidant ${relMentor}, while regularly testing ideas against the formidable intellect of ${relRival}.`,
    `Enjoys deep personal camaraderie with veteran colleagues from early field expeditions, meeting regularly with ${relMentor} for tea and historical reflection.`,
    `Draws immense creative energy from friendships with visiting astronomers, botanical architects, and the seasoned counsel of ${relMentor}.`,
  ];
  const friendships = pick(friendOptions, lifeStream(ctx, "people_friends"));

  const socialOptions = [
    `Fully integrated into the High Assembly of Stewards and the neighborhood ecological cooperative of their residential ward.`,
    `A respected voice within the Interstellar Knowledge Exchange and the local chamber music society.`,
    `Active participant in the weekly civic forum where residents deliberate municipal budgets and cultural festivals.`,
    `Connected to a wide network of researchers, artisans, and public service volunteers across five orbital rings.`,
  ];
  const socialCircle = pick(socialOptions, lifeStream(ctx, "people_social"));

  // 7. Formative Years
  const childhoodOptions = [
    `Spent childhood in the low-gravity nurseries of ${universe.name}, exploring relativistic physics and ecology through interactive tactile simulations.`,
    `Raised in a quiet garden sector where elders encouraged hands-on experimentation with automated tools and living plants from early youth.`,
    `Formed in an open-air academic community where children participated directly in neighborhood maintenance and democratic assemblies.`,
    `Grew up observing the majestic movement of solar collector swarms through the transparent domes of an orbital habitat station.`,
    `Nurtured in an environment where artistic creation, scientific inquiry, and cooperative play were treated as equally foundational to maturity.`,
    `Spent formative adolescent years assisting in community arboretums, learning the delicate chemistry that keeps artificial biospheres alive.`,
    `Immersed from birth in an unhurried, screen-calm culture that prioritized deep listening, oral storytelling, and mathematical logic.`,
    `Educated within a vibrant multi-species enclave where mutual respect between biological and synthetic minds was learned as second nature.`,
  ];
  const childhood = pick(childhoodOptions, lifeStream(ctx, "form_child"));

  const educationOptions = [
    `Completed comprehensive studies at the Central Epistemic Academy, mastering non-linear system dynamics, bio-ethics, and orbital engineering.`,
    `Graduated from the Guild of Stellar Stewards with highest peer commendations for independent research on closed-loop thermodynamic balances.`,
    `Received classical tuition in macroscopic physics and moral philosophy, followed by seven years of accredited field residency in the outer rings.`,
    `Trained through rotational apprenticeships across four distinct sectors, acquiring hands-on mastery of mechanical, biological, and computational craft.`,
    `Completed rigorous self-directed inquiries audited by public scholar juries, defending original theorems on ecological carrying capacities.`,
    `Immersed in advanced laboratory practicums focusing on relativistic telemetry, active magnetic shielding, and automated molecular synthesis.`,
    `Educated through the Generational Continuity Curriculum, emphasizing historical analysis of fallen civilizations to cultivate deep caution.`,
    `Mastered the dual disciplines of structural engineering and restorative consensus arbitration at the Interplanetary Institute of Governance.`,
  ];
  const education = pick(educationOptions, lifeStream(ctx, "form_edu"));

  // Reusing base.choices for decisions, achievements, setbacks, turningPoints
  const decisions = [
    base.choices[0]?.choice || "Choosing physical embodiment over disembodied algorithmic upload.",
    base.choices[1]?.choice || "Reinvesting personal energy credits into public biosphere protection rather than private compute expansion.",
    base.choices[2]?.choice || "Refusing to weaponize relativistic propulsion relays during border friction.",
    "Declining a prestigious central bureaucratic chair to remain an active field steward on the orbital perimeter.",
  ].slice(0, 4);

  const achievements = base.dossier.achievements.length >= 3
    ? base.dossier.achievements.slice(0, 3)
    : [
        "Stabilized the magnetic containment lattice during the Century Meridian solar flare event.",
        "Synthesized the primary ecological harmony protocol linking terrestrial and orbital biomes.",
        "Established an open-source technical repository guaranteeing zero-cost life-support access for frontier settlements.",
      ];

  const setbacks = base.dossier.failures.length >= 2
    ? base.dossier.failures.slice(0, 3)
    : [
        "Experienced temporary telemetry decoupling during the early deep-space sensor array collapse.",
        "Failed to prevent the temporary abandonment of an unstable outer Lagrange habitat early in their career.",
      ];

  const turningPoints = [
    base.story.turningPoint || "The historic decision to embrace physical hybridity over total digital upload.",
    "The successful resolution of the Sector 9 atmospheric imbalance during the Great Alignment.",
    "The establishment of the Interplanetary Biosphere Trust that permanently protected native biomes from industrial mining.",
  ].slice(0, 3);

  // 8. Society & Institutions
  const societySummary = `${civ.name} operates as ${civ.blurb.toLowerCase()}`;
  const societyLaws = civ.laws;
  const societyCulture = civ.culture;
  const societyInstitutions = civ.institutions;
  const societyExpectations = civ.expectations;
  const societyOpportunity = civ.opportunity;

  // 9. World & Technology
  const technology = pick(civ.technologies, lifeStream(ctx, "world_tech"));
  const science = pick(civ.sciences, lifeStream(ctx, "world_sci"));
  const geography = pick(civ.geographies, lifeStream(ctx, "world_geo"));
  const history = pick(civ.histories, lifeStream(ctx, "world_hist"));

  let environmentWorld: string;
  if (condition === "hostile") {
    environmentWorld = `A harshly energetic stellar realm where biological fragility has been transcended by resilient energy-lattice containment fields and non-corporeal computational manifolds.`;
  } else if (condition === "marginal") {
    environmentWorld = `An engineered planetary environment where massive atmospheric processors and orbital mirror arrays maintain a carefully balanced artificial biosphere across formerly barren rock.`;
  } else {
    environmentWorld = `A radiant, pristine planetary paradise where lush living forests and crystalline blue oceans thrive beneath the protective magnetic envelopes of the orbital ring system.`;
  }

  // 10. How This World Differs from Earth
  const howDiffersHeadlineOptions = [
    `A civilization that replaced financial accumulation with thermodynamic stewardship and deep-time care.`,
    `An advanced post-scarcity order that solved physical want while embracing the profound ethics of cosmic restraint.`,
    `A society where monumental technological power is permanently subordinated to the flourishing of conscious life.`,
    `An epochal federation that eliminated poverty and warfare by anchoring status in verifiable public service.`,
  ];
  const howDiffersHeadline = pick(howDiffersHeadlineOptions, lifeStream(ctx, "diff_head"));

  const howDiffersP1Options = [
    `Where contemporary Earth measures wealth through abstract financial ledgers and debt instruments, ${civ.name} organizes its economy around ${civ.wealthConcept.toLowerCase()}. Material reproduction is automated and universally free, completely eliminating the anxiety of survival that dominates modern terrestrial existence.`,
    `Unlike modern Earth, where economic systems incentivize continuous environmental extraction, this civilization treats planetary and stellar stability as an inviolable sacred trust. Wealth cannot be hoarded or converted into private coercive power over other citizens.`,
    `In direct contrast to Earth's market dynamics, status in this society is decoupled from consumption. Those who live with the greatest restraint and contribute the most durable public works are accorded the highest esteem in civic assemblies.`,
    `While contemporary Earth struggles with privatized infrastructure and planned obsolescence, this world mandates open-source durability and universal access across every technology from water scrubbers to interstellar propulsion.`,
  ];

  const howDiffersP2Options = [
    `Human communication has transcended polarized algorithmic outrage through transparent consensus institutions. ${civ.culture} This allows planetary decisions to be formulated through empirical simulation and deep philosophical deliberation rather than tribal rhetoric.`,
    `Governance operates on millennial horizons rather than quarterly election cycles. Every civic initiative must pass rigorous deep-time audits demonstrating that it will benefit descendants living thirty generations hence, preventing the short-sighted ecological crises that plague modern Earth.`,
    `Social hierarchy has been radically reimagined: power does not derive from capital, media sensationalism, or inherited nobility, but from verified competence and selfless devotion to the common welfare.`,
    `The pervasive isolation and digital alienation common on modern Earth are unknown here. Physical communities are designed for walkability, intimate neighborly cooperation, and regular participatory festivals that foster resilient lifetime bonds.`,
  ];

  const howDiffersP3Options = [
    `Yet this paradise is not without real friction: ${civ.scarcityTradeoff} Citizens must continually balance monumental ambition against severe thermodynamic and social realities, ensuring that their immense power never curdles into hubris.`,
    `The cost of this harmony is real: ${civ.scarcityTradeoff} Every person understands that living in an interconnected cosmic order requires active personal discipline, constant vigilance, and the maturity to accept shared limits.`,
    `Rather than presenting a frictionless utopia, this civilization wrestles with its own distinct vulnerabilities: ${civ.scarcityTradeoff} The drama of human life persists, elevated to a cosmic scale where individual ethical choices echo across star systems.`,
    `Even with near-limitless energy, the fundamental challenges of consciousness remain: ${civ.scarcityTradeoff} It is within these very tradeoffs that citizens like ${base.name} discover their highest purpose and deepest fulfillment.`,
  ];

  const howDiffersParagraphs = [
    pick(howDiffersP1Options, lifeStream(ctx, "diff_p1")),
    pick(howDiffersP2Options, lifeStream(ctx, "diff_p2")),
    pick(howDiffersP3Options, lifeStream(ctx, "diff_p3")),
  ];

  const keyDifferences = [
    {
      title: "Non-Financialized Resource Allocation",
      text: `Money and private debt do not exist; society operates via ${civ.wealthConcept.toLowerCase()} ensuring absolute freedom from material insecurity.`,
    },
    {
      title: "Millennial Horizon Governance",
      text: `Public policies are evaluated across multi-century timeframes through ${civ.institutions}, completely preventing the short-term extraction cycles of Earth.`,
    },
    {
      title: "Open-Source Technological Substrate",
      text: `All manufacturing schematics and scientific discoveries are open public property, eliminating proprietary monopolies and artificial scarcity.`,
    },
    {
      title: "Thermodynamic and Social Restraint",
      text: `${civ.scarcityTradeoff}`,
    },
  ];

  // 11. A Day in This Life
  // Spec: exactly "A Day in This Life"; exactly 8 beats; each beat 2-4 sentences mixing work, a named relationship scene, a small friction, a moment of meaning; coherent with universe.
  // Mention base.dossier.relationships names (relMentor, relRival, relCompanion) and workplace/occupation.

  const beat1Text = `Awakens naturally in ${dwelling.toLowerCase()} as the soft illumination of ${universe.name}'s star filters through panoramic crystalline louvers. Takes a quiet moment to drink fresh roasted chicory while reviewing overnight telemetry logs from the central array. A brief sensor warning flags a minor thermal spike in Sector 4's conduit, which ${base.name} calmly logs for the morning inspection shift. The tranquil stillness of early dawn provides a grounding start before the day's responsibilities begin.`;

  const beat2Text = `Travels via ${transport.toLowerCase()} toward ${workplace.toLowerCase()}, arriving promptly for the morning shift. Greeted at the entrance by junior apprentices who have already prepared the diagnostic calibrations for today's tasks in ${base.occupation.toLowerCase()}. A minor friction arises when an automated delivery drone arrives with mismatched coupling gaskets, requiring ${base.name} to manually re-route the supply request through the central clearinghouse. With patient precision, the morning maintenance checks are set in motion without delaying the schedule.`;

  const beat3Text = `Steps onto the main operations gantry to perform the primary technical calibrations required for ${base.occupation.toLowerCase()}. Monitors the delicate balance of energy vectors, noting with satisfaction that the modifications implemented last cycle have reduced systemic harmonic jitter by twelve percent. A communication ping arrives from senior mentor ${relMentor}, offering thoughtful words of encouragement regarding the upcoming council vote. This brief exchange of generational wisdom reinforces ${base.name}'s sense of continuity within the order.`;

  const beat4Text = `Pauses at midday for a wholesome communal meal in the central dining pavilion, sharing bread, roasted vegetables, and fresh goat cheese with team members. The atmosphere grows spirited as ${base.name} encounters institutional rival ${relRival}, who challenges the efficiency projections of the proposed expansion. Rather than escalating into bitter acrimony, their debate remains intellectually rigorous and mutually respectful, highlighting two valid perspectives on systemic risk. ${base.name} leaves the table invigorated by the exchange, having refined several key arguments.`;

  const beat5Text = `Spends the early afternoon in deep consultation with regional delegates, reviewing resource allocation petitions from the outer perimeter wards. Explains with clear, unpretentious diagrams why thermodynamic caps require a three-cycle phased deployment rather than an immediate power surge. When a frustrated ward representative voices anxiety over winter reserves, ${base.name} listens with genuine empathy and adjusts the secondary battery allocations to guarantee their security. This delicate diplomatic synthesis dissolves tension and restores communal trust.`;

  const beat6Text = `Returns to personal benchwork in ${workplace.toLowerCase()} to conduct delicate precision testing on an experimental component. Works in focused silence for two hours, shaping materials with the tactile patience of a master craftsman who refuses to leave everything to automated machines. A sudden minor power fluctuation causes a test sequence to reset, requiring ${base.name} to recalibrate the diagnostic sensors from scratch without annoyance. By late afternoon, the component performs within flawless tolerances, ready for permanent deployment.`;

  const beat7Text = `Departs the workstation as twilight turns the sky to deep indigo and copper, returning home to meet ${relCompanion} for a stroll through the neighborhood arboretum. They walk hand in hand along the curved gravel paths, inhaling the sweet perfume of night-blooming jasmine while discussing the small triumphs and frustrations of their day. ${relCompanion} shares news of their own creative projects, providing an intimate, loving counterweight to ${base.name}'s vast cosmic duties. They purchase fresh figs and warm flatbread from a local artisan before heading inside.`;

  const beat8Text = `Enjoys a simple, candle-lit dinner with ${relCompanion} at the low cedar table, accompanied by the gentle acoustic music of a wooden lute floating through the open window. Afterward, sits in the quiet study to record the day's critical observations and personal gratitudes in a handwritten leather journal. Checks the primary habitat monitors one final time—all pressures, oxygen levels, and energy fluxes resting in perfect equilibrium. Retires to bed feeling a profound, tranquil peace, knowing another day was dedicated to the enduring protection of conscious life.`;

  const dayBeats: LifeBeat[] = [
    { time: "05:30", heading: "Dawn Light & Telemetry", text: beat1Text },
    { time: "07:30", heading: "Arrival at the Station", text: beat2Text },
    { time: "09:45", heading: "Operational Precision", text: beat3Text },
    { time: "12:15", heading: "Midday Repast & Debate", text: beat4Text },
    { time: "14:30", heading: "Consensus Mediation", text: beat5Text },
    { time: "16:45", heading: "Hands-on Benchwork", text: beat6Text },
    { time: "19:00", heading: "Twilight Walk with Kin", text: beat7Text },
    { time: "21:30", heading: "Evening Peace & Archival Rest", text: beat8Text },
  ];

  const dayClosing = `As starlight washes softly across the curved terraces of the city, serene silence settles over the home, accompanied by the quiet knowledge that another day's labor left the commonwealth safer and wiser than before.`;

  // 12. Three Possible Futures
  // Spec: exactly "Three Possible Futures"; exactly 3 paths ids a,b,c with flavors "Most natural","Ambitious","Wildcard"; each 3 opportunities, 3 challenges; disclaimer states these are story branches, not predictions.
  const futures: LifeFuturePath[] = [
    {
      id: "a",
      name: "Grand Chancellor of the Stewardship Commons",
      flavor: "Most natural",
      summary: `Gradual and dignified elevation to the supreme executive council of ${civ.name}, formalizing systemic stewardship standards across the entire star federation.`,
      opportunities: [
        `Codifying permanent thermodynamic safety charters adopted by seventy orbital colonies.`,
        `Directing the multi-century expansion of open-access public research laboratories.`,
        `Mentoring an entire generation of incoming civic leaders in restorative consensus governance.`,
      ],
      challenges: [
        `Managing political friction with conservative factions resisting the expansion of public reserves.`,
        `Enduring the immense psychological weight of adjudicating civilization-scale energy triage.`,
        `Sacrificing solitary craft and domestic leisure time for continuous assembly deliberation.`,
      ],
      consequences: `Universal historical recognition as one of the most benevolent and effective administrators in the modern epoch of ${universe.name}.`,
    },
    {
      id: "b",
      name: "Commander of the Relativistic Seed Ark",
      flavor: "Ambitious",
      summary: `Stepping beyond familiar solar borders to lead a bold relativistic expedition toward an uncolonized star cluster, establishing an entirely new daughter civilization from first principles.`,
      opportunities: [
        `Founding a newly chartered human commonwealth unburdened by historical institutional calcification.`,
        `Pioneering novel relativistic propulsion architectures and deep-space closed-loop biomes.`,
        `Discovering exotic physics and primordial planetary formations in virgin interstellar space.`,
      ],
      challenges: [
        `Irreversible separation from familiar domestic comforts, friends, and planetary culture.`,
        `Navigating multi-decade communications delays with the ancestral homeworld.`,
        `Facing unpredictable cosmic hazards and equipment fatigue across light-years of void.`,
      ],
      consequences: `Permanent immortality in interstellar chronicles as a founding patriarch of a thriving multi-world diaspora.`,
    },
    {
      id: "c",
      name: "Re-Wilder of the Primordial Cradle",
      flavor: "Wildcard",
      summary: `Resigning from all administrative and technological offices to spend personal decades living in the wild mountain sanctuaries, restoring ancient ancestral biomes with manual tools and unhurried meditation.`,
      opportunities: [
        `Experiencing unmediated physical communion with wild nature, free from computational feeds.`,
        `Restoring extinct heirloom flora and fauna species through tactile, patient horticulture.`,
        `Authoring deeply influential poetic treatises on the spiritual necessity of technological restraint.`,
      ],
      challenges: [
        `Relinquishing vast institutional authority and the sophisticated comforts of advanced technology.`,
        `Facing harsh physical weather, physical labor, and the vulnerabilities of unshielded biology.`,
        `Being misunderstood by ambitious peers as having abandoned duty for nostalgic solitude.`,
      ],
      consequences: `Achieving profound personal enlightenment and leaving behind a flourishing, untouched planetary wilderness sanctuary for future seekers.`,
    },
  ];

  const futuresDisclaimer = `These pathways represent imaginative narrative branches exploring potential evolutions of this persona's trajectory; they are works of speculative fiction, not real-world predictions or destiny.`;

  // 13. Timeline
  // Spec: 9-10 entries, ascending age, kinds covering origin, education, choice, achievement, setback, turning, present, future (present = identity.age; future entries > age). label like "Age 6". Reuse base.choices and base.story.turningPoint facts consistently.
  const ageOrigin = Math.max(5, Math.floor(age * 0.12));
  const ageEdu = Math.max(ageOrigin + 6, Math.floor(age * 0.28));
  const ageChoice = Math.max(ageEdu + 4, Math.floor(age * 0.45));
  const ageAchieve = Math.max(ageChoice + 4, Math.floor(age * 0.6));
  const ageSetback = Math.max(ageAchieve + 3, Math.floor(age * 0.72));
  const ageTurning = Math.max(ageSetback + 2, age - 4);
  const agePresent = age;
  const ageFuture1 = age + 8;
  const ageFuture2 = age + 18;

  const timeline: LifeTimelineEntry[] = [
    {
      age: ageOrigin,
      label: `Age ${ageOrigin}`,
      title: "Awakening in the Spire",
      text: (base.story?.origin ?? "").slice(0, 150) + "...",
      kind: "origin",
    },
    {
      age: ageEdu,
      label: `Age ${ageEdu}`,
      title: "Mastery of Systemic Dynamics",
      text: `Completes formal certifications at the Epistemic Academy, mastering macroscopic thermodynamics and ${base.occupation.toLowerCase()}.`,
      kind: "education",
    },
    {
      age: ageChoice,
      label: `Age ${ageChoice}`,
      title: "The Crossroads of Allegiance",
      text: base.choices[0]?.choice || "Elects physical field stewardship over comfortable central bureaucratic tenure.",
      kind: "choice",
    },
    {
      age: ageAchieve,
      label: `Age ${ageAchieve}`,
      title: "Breakthrough in Harmonic Equilibrium",
      text: base.dossier.achievements[0] || "Successfully stabilized the primary magnetic containment shield during the solar meridian shift.",
      kind: "achievement",
    },
    {
      age: ageSetback,
      label: `Age ${ageSetback}`,
      title: "The Crucible of Containment Failure",
      text: base.dossier.failures[0] || "Managed a severe telemetry decoupling in the outer sector, preventing cataclysm through sheer personal stamina.",
      kind: "setback",
    },
    {
      age: ageTurning,
      label: `Age ${ageTurning}`,
      title: "The Turning Point of Stewardship",
      text: (base.story?.turningPoint ?? "").slice(0, 150) + "...",
      kind: "turning",
    },
    {
      age: agePresent,
      label: `Age ${agePresent}`,
      title: "Prime Authority as " + base.title,
      text: `Currently exercises serene executive stewardship over the energy fluxes and public welfare of ${universe.name}.`,
      kind: "present",
    },
    {
      age: ageFuture1,
      label: `Age ${ageFuture1}`,
      title: "Ratification of the Centennial Charter",
      text: `Anticipated to lead the inter-system constitutional convention that codifies deep-time ecological limits into interstellar law.`,
      kind: "future",
    },
    {
      age: ageFuture2,
      label: `Age ${ageFuture2}`,
      title: "Commissioning of the Interstellar Seed",
      text: `Projected to oversee the launch of the first generation ark carrying living biomes toward the galactic arm.`,
      kind: "future",
    },
  ];

  // 14. Earth Comparison
  // Spec: basis "general-reference"; basisNote explains Earth column is general reference for a typical modern Earth life, not facts about visitor;
  // rows: exactly 8, one per EarthDimension in this order: Occupation, Social status, Financial circumstances, Relationships, Freedoms, Daily life, Available technology, Future opportunities.
  // divergences 3-4 with unique ids "d1".."d4"; every row.divergenceId references one.
  // visitorSignals 3-5 strings saying which of visitor's answers (use ctx.resolved; mention when generator filled a skipped answer) shaped which part of this life.

  const divergences: EarthDivergence[] = [
    {
      id: "d1",
      title: "Post-Scarcity Energy Mastery & Abolition of Speculative Debt",
      text: `In this reality, total solar radiation capture through Dyson collector swarms eliminated energy scarcity and rendered fiat currency, speculative finance, and private debts obsolete.`,
      effects: `Material survival is unconditionally guaranteed to all citizens, liberating humanity from wage labor to pursue deep science, craft, and ecological stewardship.`,
    },
    {
      id: "d2",
      title: "Deep-Time Consensus Governance",
      text: `Political institutions evaluate policies across multi-century horizons rather than short-term electoral cycles, using transparent open-source simulations to predict ecological feedback loops.`,
      effects: `Prevents short-sighted environmental destruction and administrative corruption, fostering a civic culture of patience, restraint, and mutual trust.`,
    },
    {
      id: "d3",
      title: "Decentralized Open-Source Knowledge Substrate",
      text: `All intellectual patents, medical formulas, and manufacturing schematics are held in open public trust, preventing private monopolies and planned obsolescence.`,
      effects: `Every community possesses the tools and legal autonomy to repair, fabricate, and adapt advanced technologies for local needs without corporate dependence.`,
    },
  ];

  const earthRowsData: Record<EarthDimension, { here: string; earth: string; because: string; divergenceId: string }> = {
    Occupation: {
      here: base.occupation,
      earth: "Specialized corporate employee, service worker, or public bureaucrat bound to employment contracts",
      because: `Because post-scarcity energy capture (d1) automated routine production, professional labor evolved into high-level planetary and stellar stewardship.`,
      divergenceId: "d1",
    },
    "Social status": {
      here: `${civ.wealthBracket} · ${socialClass}`,
      earth: "Status dictated by financial net worth, corporate job title, and media visibility",
      because: `Because speculative capital was abolished (d1), social prestige is earned solely through verified contributions to public safety and long-term wisdom (d2).`,
      divergenceId: "d1",
    },
    "Financial circumstances": {
      here: wealthText,
      earth: "Vulnerable to inflation, housing costs, interest rates, and precarious wage labor",
      because: `Because energy and automated fabrication are held in common trust (d1), citizens live entirely free from debt, rent, and material anxiety.`,
      divergenceId: "d1",
    },
    Relationships: {
      here: `Deeply bonded partnership with ${relCompanion}, lifelong mentorship with ${relMentor}, and unhurried civic fellowship`,
      earth: "Often fragmented by demanding commute times, career relocations, and digital screen overload",
      because: `Because unhurried living rhythms and walkable cities (d2) eliminate artificial economic pressure, allowing deep, enduring lifetime communities to flourish.`,
      divergenceId: "d2",
    },
    Freedoms: {
      here: "Full personal autonomy over time, creative projects, and geographic movement, bounded only by shared thermodynamic caps",
      earth: "Extensive consumer market choices constrained by work hours, financial debt, and corporate workplace surveillance",
      because: `Because universal access to open technology (d3) gives every individual absolute material independence from coercive employers.`,
      divergenceId: "d3",
    },
    "Daily life": {
      here: "A measured, contemplative cadence balancing macro-scale stewardship with manual gardening, reading, and shared evening meals",
      earth: "High-stress desk labor, traffic commutes, urgent digital notifications, and continuous algorithmic stimulation",
      because: `Because institutions prioritize acoustic quiet and attention sovereignty (d2), shielding daily life from noisy commercial advertising and manufactured urgency.`,
      divergenceId: "d2",
    },
    "Available technology": {
      here: `${technology.slice(0, 80)}`,
      earth: "Fragile microelectronics, fossil-fueled energy grids, proprietary software, and planned hardware obsolescence",
      because: `Because open-source physical engineering (d3) and Type II solar energy capture (d1) produce indestructible, fully repairable public infrastructure.`,
      divergenceId: "d3",
    },
    "Future opportunities": {
      here: "Participation in relativistic interstellar arks, deep-time planetary terraforming, and universal knowledge curation",
      earth: "Career pivots driven by market disruption, automation displacement, and ecological uncertainty within a single biosphere",
      because: `Because civilizational horizons span multiple star systems and millennia (d2), ensuring boundless avenues for meaningful discovery.`,
      divergenceId: "d2",
    },
  };

  const earthDimensionsOrder: EarthDimension[] = [
    "Occupation",
    "Social status",
    "Financial circumstances",
    "Relationships",
    "Freedoms",
    "Daily life",
    "Available technology",
    "Future opportunities",
  ];

  const earthRows: EarthRow[] = earthDimensionsOrder.map((dim) => ({
    dimension: dim,
    here: earthRowsData[dim].here,
    earth: earthRowsData[dim].earth,
    because: earthRowsData[dim].because,
    divergenceId: earthRowsData[dim].divergenceId,
  }));

  // Visitor Signals: 3-5 strings saying which of visitor's answers (use ctx.resolved; mention when generator filled a skipped answer) shaped which part of this life.
  const visitorSignals: string[] = [];

  // Signal 1: Interests
  const intNames = listJoin(resolved.interests.map((i) => i));
  if (input.interests.length > 0) {
    visitorSignals.push(
      `Your chosen affinity for ${intNames} directly shaped this persona's specialized expertise in ${(expertise[0] ?? "planetary systems").toLowerCase()} and their role in ${base.occupation.toLowerCase()}.`
    );
  } else {
    visitorSignals.push(
      `Because you skipped the interest questions, the generator selected ${intNames} as the intellectual foundation for this persona's advanced technological stewardship.`
    );
  }

  // Signal 2: Strengths
  const strNames = listJoin(resolved.strengths.map((s) => s));
  if (input.strengths.length > 0) {
    visitorSignals.push(
      `Your declared strengths in ${strNames} anchored this persona's reputation for calm moral integrity and their high influence score of ${influenceScore}/100.`
    );
  } else {
    visitorSignals.push(
      `Because strengths were left unselected, the generator supplied ${strNames}, defining this self's disciplined, unhurried temperament during systemic crises.`
    );
  }

  // Signal 3: Ambition
  if (resolved.filled.ambition) {
    visitorSignals.push(
      `Because ambition was skipped, the generator assigned a drive to "${resolved.ambition}", steering this persona toward the ambitious forward horizon of ${futures[0].name}.`
    );
  } else {
    visitorSignals.push(
      `Your stated ambition to "${resolved.ambition}" guided this persona's defining turning point and their commitment to ${civ.scarcityTradeoff.toLowerCase()}`
    );
  }

  // Signal 4: Life Path
  if (resolved.filled.lifePath) {
    visitorSignals.push(
      `Because life path was left open, the generator channeled your profile through "${resolved.lifePath}", placing this self within the noble architecture of ${civ.name}.`
    );
  } else {
    visitorSignals.push(
      `Your selected life path of "${resolved.lifePath}" defined this self's grounded, hands-on relationship with physical materials in ${workplace.toLowerCase()}.`
    );
  }

  return {
    engineVersion: ENGINE_VERSION,
    identity: {
      fullName: base.name,
      age,
      ageNote,
      personalitySummary: base.dossier.personality,
      biography,
    },
    work: {
      occupation: base.occupation,
      expertise,
      careerHistory,
      responsibilities,
      workplace,
    },
    standing: {
      wealthBracket,
      wealthText,
      socialClass,
      reputation,
      prestige,
      influence,
      influenceLevel: influenceScore,
      economicCircumstances,
    },
    home: {
      dwelling,
      environment,
      lifestyle,
      possessions,
      transport,
      routine,
    },
    people: {
      family,
      relationships: relationshipsText,
      friendships,
      socialCircle,
    },
    formative: {
      childhood,
      education,
      decisions,
      achievements,
      setbacks,
      turningPoints,
    },
    society: {
      summary: societySummary,
      laws: societyLaws,
      culture: societyCulture,
      institutions: societyInstitutions,
      expectations: societyExpectations,
      opportunity: societyOpportunity,
    },
    world: {
      technology,
      science,
      geography,
      history,
      environment: environmentWorld,
    },
    howDiffers: {
      headline: howDiffersHeadline,
      paragraphs: howDiffersParagraphs,
      keyDifferences,
    },
    dayInLife: {
      title: "A Day in This Life",
      beats: dayBeats,
      closing: dayClosing,
    },
    futures: {
      title: "Three Possible Futures",
      paths: futures,
      disclaimer: futuresDisclaimer,
    },
    timeline,
    earthComparison: {
      basis: "general-reference",
      basisNote: `The Earth column is a general reference reflecting conditions of a typical modern Earth professional life, not personal facts about the visitor, because the questionnaire gathers no private terrestrial records.`,
      rows: earthRows,
      divergences,
      visitorSignals,
    },
  };
}
